// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAccountShipTosDataView } from "@insite/client-framework/Store/Data/AccountShipTos/AccountShipTosSelectors";
import loadAccountShipToCollection from "@insite/client-framework/Store/Data/AccountShipTos/Handlers/LoadAccountShipToCollection";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import saveShipToCollection from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SaveShipToCollection";
import updateSearchFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { AccountShipToModel, CostCodeModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import Select from "@steiner/MobiusOverrides/Select/Select";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isModalOpen: boolean;
    closeModalHandler: () => void;
    extendedStyles?: AssignShipToAddressModalStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    accountShipTosDataView: getAccountShipTosDataView(state, state.pages.userSetup.getAccountShipToCollectionParameter),
    parameter: state.pages.userSetup.getAccountShipToCollectionParameter,
    // XNG-Changes
    billToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AssignShipToAddressModalStyles {
    modal?: ModalPresentationProps;
    shipToContainer?: GridContainerProps;
    shipToNumberGridItem?: GridItemProps;
    shipToNumberLabel?: TypographyPresentationProps;
    shipToNumberText?: TypographyPresentationProps;
    addressInfoGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    assignShippingAddressGridItem?: GridItemProps;
    assignShippingAddressCheckbox?: CheckboxPresentationProps;
    pagination?: PaginationPresentationProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    // XNG-Changes
    selectAllWrapper?: InjectableCss;
    costCodeGridItem?: GridItemProps;
}

export const assignShipToAddressModalStyles: AssignShipToAddressModalStyles = {
    modal: { sizeVariant: "large" },
    shipToContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    shipToNumberGridItem: {
        width: [4, 2, 2, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    shipToNumberLabel: {
        css: css`
            margin-bottom: 5px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    addressInfoGridItem: { width: [8, 6, 6, 6, 7] },
    assignShippingAddressGridItem: { width: [12, 4, 4, 4, 3] },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    saveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const AssignShipToAddressModal = ({
    isModalOpen,
    accountShipTosDataView,
    parameter,
    closeModalHandler,
    extendedStyles,
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
    // XNG-Changes
    billToState,
}: Props) => {
    useEffect(() => {
        if (!accountShipTosDataView.value && !accountShipTosDataView.isLoading) {
            loadAccountShipToCollection(parameter);
        }
    });

    useEffect(() => {
        if (accountShipTosDataView.value) {
            setShipToCollection(accountShipTosDataView.value);
            setPagination(accountShipTosDataView.pagination);
            // XNG-Changes
            setOriginalShipToCollection(accountShipTosDataView.value);
        }
    }, [accountShipTosDataView.value]);

    const [shipToCollection, setShipToCollection] = React.useState<AccountShipToModel[] | undefined>();
    const [pagination, setPagination] = useState<PaginationModel | null | undefined>();
    const [isSaving, setIsSaving] = useState(false);

    const [styles] = useState(() => mergeToNew(assignShipToAddressModalStyles, extendedStyles));

    // <XNG-Changes>
    const [originalShipToCollection, setOriginalShipToCollection] = React.useState<AccountShipToModel[] | undefined>();
    const [selectAll, setSelectAll] = useState(true);
    const [costCodes, setCostCodes] = useState<CostCodeModel[]>([]);

    useEffect(() => {
        if (billToState.value) {
            setCostCodes(billToState.value.costCodes || []);
        }
    }, [billToState]);
    // </XNG-Changes>

    const assignShippingAddressChangeHandler = (value: boolean, index: number) => {
        if (!shipToCollection) {
            return;
        }

        const currentShipToCollection = cloneDeep(shipToCollection);
        currentShipToCollection[index].assign = value;
        setShipToCollection(currentShipToCollection);

        // XNG-Chanegs
        // Check if all items are selected
        const allSelected = currentShipToCollection.every(shipTo => shipTo.assign);
        setSelectAll(allSelected);

        setIsSaving(true);
        saveShipToCollection({
            shipToCollection: currentShipToCollection,
            onSuccess: () => {
                setIsSaving(false);
            },
            onError: () => {
                setIsSaving(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const handleChangePage = (page: number) => {
        updateSearchFields({ page });
    };

    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    const saveButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (!shipToCollection) {
            return;
        }

        closeModalHandler();
    };
    // XNG-Changes: Fix OOTB issue for cancel button not cancelling selection
    const CloseButtonAndIconClickHandler = () => {
        closeModalHandler();
        setShipToCollection(originalShipToCollection);
    };

    // XNG-Changes
    const toggleSelectAll = () => {
        if (shipToCollection) {
            const updatedCollection = shipToCollection.map(shipTo => ({
                ...shipTo,
                assign: !selectAll, // Toggle all assign values based on selectAll state
            }));
            setShipToCollection(updatedCollection);
            setSelectAll(!selectAll);
        }
    };

    const handleCostCodeChange = (e: React.ChangeEvent<HTMLSelectElement>, index: number) => {
        if (shipToCollection) {
            const updatedCollection = [...shipToCollection];
            updatedCollection[index] = { ...updatedCollection[index], costCode: e.target.value };
            setShipToCollection(updatedCollection);
        }
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Assign Ship To Address")}
            isOpen={isModalOpen}
            // XNG-Changes
            handleClose={CloseButtonAndIconClickHandler}
            data-test-selector="assignShiptoAddressModal"
        >
            {/* XNG-Changes */}
            {/* adding LoadingOverlay, TableCounter and Select All checkbox */}
            <LoadingOverlay loading={accountShipTosDataView.isLoading}>
                <TableCounter pagination={accountShipTosDataView.pagination as PaginationModel}>
                    {shipToCollection && shipToCollection.length > 1 && (
                        <StyledWrapper {...styles.selectAllWrapper}>
                            <Checkbox
                                {...styles.assignShippingAddressCheckbox}
                                checked={selectAll}
                                onChange={toggleSelectAll}
                                data-test-selector="assignShipToAddressModal_selectAll"
                            >
                                {translate("Select All")}
                            </Checkbox>
                        </StyledWrapper>
                    )}
                    {shipToCollection?.map((shipTo, index) => (
                        <GridContainer key={shipTo.shipToNumber} {...styles.shipToContainer}>
                            <GridItem {...styles.shipToNumberGridItem}>
                                <Typography as="p" {...styles.shipToNumberLabel} id="shipToNumber">
                                    {translate("Ship To #")}
                                </Typography>
                                <Typography as="p" {...styles.shipToNumberText} aria-labelledby="shipToNumber">
                                    {shipTo.shipToNumber}
                                </Typography>
                            </GridItem>
                            {/* XNG-Changes */}
                            <GridItem {...styles.costCodeGridItem}>
                                <Select
                                    data-test-selector="assignShipToAddressModal_costCode"
                                    onChange={e => handleCostCodeChange(e, index)}
                                    value={shipTo.costCode}
                                >
                                    <option value="">{translate("Select Cost Code")}</option>
                                    {costCodes.map(cc => (
                                        <option key={cc.id} value={cc.costCode}>
                                            {cc.costCode}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.addressInfoGridItem}>
                                <AddressInfoDisplay
                                    address1={shipTo.address}
                                    city={shipTo.city}
                                    state={shipTo.state}
                                    postalCode=""
                                    extendedStyles={styles.addressInfoDisplay}
                                />
                            </GridItem>
                            <GridItem {...styles.assignShippingAddressGridItem}>
                                <Checkbox
                                    {...styles.assignShippingAddressCheckbox}
                                    checked={shipTo.assign}
                                    onChange={(e, value) => assignShippingAddressChangeHandler(value, index)}
                                    disabled={
                                        shipTo.shipToNumber.length === 0 || isSaving || accountShipTosDataView.isLoading
                                    }
                                    data-test-selector="assignShipToAddressModal_assign"
                                    data-test-key={shipTo.shipToNumber}
                                >
                                    {translate("Assign Shipping Address")}
                                </Checkbox>
                            </GridItem>
                        </GridContainer>
                    ))}
                </TableCounter>
                {pagination && pagination.totalItemCount > 1 && (
                    <Pagination
                        {...styles.pagination}
                        resultsCount={pagination.totalItemCount}
                        currentPage={pagination.page}
                        resultsPerPage={pagination.pageSize}
                        resultsPerPageOptions={pagination.pageSizeOptions}
                        onChangePage={handleChangePage}
                        onChangeResultsPerPage={handleChangeResultsPerPage}
                        prevPage={pagination.prevPageUri}
                        nextPage={pagination.nextPageUri}
                    />
                )}
                <StyledWrapper {...styles.buttonsWrapper}>
                    {/* XNG-Changes */}
                    <Button {...styles.cancelButton} onClick={CloseButtonAndIconClickHandler} aria-label="Cancel">
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.saveButton}
                        onClick={e => saveButtonClickHandler(e)}
                        data-test-selector="assignShipToAddressModal_saveButton"
                        // XNG-Changes
                        aria-label="Save"
                    >
                        {translate("Save")}
                    </Button>
                </StyledWrapper>
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AssignShipToAddressModal);
